import one from '@mailobj-browser/front/js/selectors/one.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import capture from '@mailobj-browser/front/js/events/options/capture.js'
import click from '@mailobj-browser/front/js/events/types/click.js'
import {
  tapUp
} from '@mailobj-browser/front/js/events/listeners/builtins/tap.js'
import closest from '@mailobj-browser/front/js/selectors/closest.js'
import preventDefault
  from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import stopImmediatePropagation
  from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import { render } from '#utilities/js/template/template.js'
import keyDown from '@mailobj-browser/front/js/events/types/keyDown.js'
import focus from '@mailobj-browser/front/js/events/types/focus.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import { elements } from '#components/generics/js/styles.js'
import { modifiers } from '#components/mail/js/styles.js'
import matchBadgeNumbers
  from '#components/generics/js/utils/matchBadgeNumbers.js'

const badgesInitialValues = new WeakMap()

const expand = object(null, {
  ArrowLeft: false,
  ArrowRight: true,
})

// NOTE:
// On intercepte le click en capture pour éviter que le menu mobile se ferme quand on déroule un menu ou que d’autres events se déclenchent au click
const clickOnItemIcon = object(listener, {
  capture,
  type: click,
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: () => { },
})

const tapUpItemIcon = object(tapUp, {
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: iconContainer => toggleItemExpansion(iconContainer)
})

const focusItem = object(listener, {
  type: focus,
  // hooks: array([
  //   preventDefault,
  //   stopImmediatePropagation
  // ]),
  task: itemLink => {
    if (itemLink.hasAttribute('aria-expanded')) {
      arrowKeyOnItem.listen(itemLink)
    }
  }
})

const arrowKeyOnItem = object(listener, {
  type: keyDown,
  passive,
  task: (itemLink, event) => {
    const { key } = event

    if (expand[key] !== undefined) {
      const iconContainer = one('[role="treeitem"] > [data-type="expander"]', itemLink)

      toggleItemExpansion(iconContainer, expand[key])
    }
  }
})

export function updateBadge (text) {
  if (text === this.textContent) {
    return
  }

  if (text !== undefined) {
    this.textContent = text
    badgesInitialValues.set(this, text)
  }

  const item = closest('[data-contract~="tree.item"]', this)

  computeItemBadgeText(item)

  const parentTreeItem = closest('[data-contract~="tree.item"]', item.parentElement)

  if (parentTreeItem) {
    const parentBadge = one(`.${elements.badge}`, parentTreeItem)

    // NOTE: Condition ajoutée à cause d’un problème de parsing des dossiers qui finissaient par un `*`,
    // dans ce cas l’arborescence étaient incohérente ce qui déclenchait une boucle infinie.
    if (parentBadge !== this) {
      parentBadge?.updateBadge?.()
    }
  }
}

const computeItemBadgeText = (treeItemContainer, expandedState) => {
  const treeItem = one('[role="treeitem"]', treeItemContainer)
  const badgesUnderItem = all(`.${elements.badge}`, treeItemContainer)

  if (badgesUnderItem.length <= 1) {
    return
  }

  if (expandedState === undefined) {
    const { ariaExpanded } = treeItem

    expandedState = ariaExpanded === 'true'
  }

  const actualBadge = one(`.${elements.badge}`, treeItemContainer)

  if (actualBadge) {
    if (!badgesInitialValues.has(actualBadge)) {
      const { textContent } = actualBadge
      badgesInitialValues.set(actualBadge, textContent)
    }

    if (expandedState) {
      const replaceText = badgesInitialValues.get(actualBadge) ?? ''

      if (matchBadgeNumbers(replaceText).unread) {
        treeItemContainer.classList.add(modifiers.menu_item.has_unseen)
      } else {
        treeItemContainer.classList.remove(modifiers.menu_item.has_unseen)
      }

      actualBadge.textContent = badgesInitialValues.get(actualBadge) ?? ''
    } else {
      const { unread, total } = badgesUnderItem
        .values()
        .map(badge => badgesInitialValues.get(badge) ?? badge.textContent)
        .filter(text => text.length)
        .reduce((acc, text) => {
          const { unread, total } = matchBadgeNumbers(text)

          acc.unread += unread
          acc.total += total

          return acc
        }, { unread: 0, total: 0 })

      if (unread) {
        actualBadge.textContent = `${unread} / ${total}`
        treeItemContainer.classList.add(modifiers.menu_item.has_unseen)
      } else if (total) {
        actualBadge.textContent = total
        treeItemContainer.classList.remove(modifiers.menu_item.has_unseen)
      }
    }
  }
}

const toggleItemExpansion = async (iconContainer, expand) => {
  const treeItemContainer = closest('[data-contract~="tree.item"]', iconContainer)
  const treeItem = one('[role="treeitem"]', treeItemContainer)
  const isExpanded = treeItem.getAttribute('aria-expanded') === 'true'

  if (isExpanded === expand) {
    return
  }

  const newExpandedState = expand ?? !isExpanded
  const template = one(':scope > template', treeItemContainer)
  const loadedChildren = one('[aria-expanded] ~ [role="group"]', treeItemContainer)

  computeItemBadgeText(treeItemContainer, newExpandedState)

  if (newExpandedState) {
    iconContainer.title = closest('[data-lang-expanded]', iconContainer).dataset.langExpanded
  } else {
    iconContainer.title = closest('[data-lang-collapsed]', iconContainer).dataset.langCollapsed
  }

  if (template) {
    template.id = 'tree_subitem_being_opened'
    // NOTE: aria-expanded is only present on nodes that has children, wether they are already in the DOM or not
    const loadChildren = treeItem.hasAttribute('aria-expanded') && newExpandedState && !loadedChildren
    const subitem = await render(
      template,
      false,
      object(null, {
        expand: newExpandedState,
        load_children: loadChildren,
        depth: treeItem.getAttribute('aria-level')
      })
    )

    template.removeAttribute('id')

    if (subitem) {
      subitem.id = treeItem.getAttribute('aria-owns')
      delete subitem.dataset.after
    }
  }

  treeItem.setAttribute('aria-expanded', newExpandedState)
}

export default item => {
  const itemLink = one('[role="treeitem"]', item)
  const badge = one(`.${elements.badge}`, item)

  if (badge) {
    // @todo cette fonction est appelée par l’ancien JS, dans `init_menu_msg.js` afin
    // d’éviter qu’il override le contenu des badges. Ça permet de recalculer le
    // badge accumulé et celui de ses parents.
    badge.updateBadge = updateBadge

    computeItemBadgeText(item)
  }

  focusItem.listen(itemLink)

  const iconContainer = one('[role="treeitem"] > [data-type="expander"]', item)

  if (iconContainer) {
    tapUpItemIcon.listen(iconContainer)
    clickOnItemIcon.listen(iconContainer)
  }
}
